import React, { FC, useEffect, useRef, useState } from 'react';
import { AppTooltip } from 'ui/AppTooltip';

interface TextWithTooltipProps {
  content?: string | number | null | JSX.Element;
  className?: string;
  isMobile?: boolean;
  style?: React.CSSProperties;
}

export const TextWithTooltip: FC<TextWithTooltipProps> = ({
  content,
  className,
  isMobile,
  style,
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const getTooltipLabel = (): string => {
    if (typeof content === 'string') {
      return content;
    }

    if (typeof content === 'number') {
      return String(content);
    }

    if (React.isValidElement(content)) {
      const extractText = (node: React.ReactNode): string => {
        if (typeof node === 'string' || typeof node === 'number') {
          return String(node);
        }
        if (Array.isArray(node)) {
          return node.map(extractText).join('');
        }
        if (React.isValidElement(node)) {
          const props = node.props as { children?: React.ReactNode };
          if (props.children) {
            return extractText(props.children);
          }
        }
        return '';
      };
      const props = content.props as { children?: React.ReactNode };
      return extractText(props.children || '');
    }
    return '';
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (!textRef.current) return;
      const element = textRef.current;
      setIsOverflowing(element.scrollWidth > element.clientWidth + 1);
    };

    const rafId = requestAnimationFrame(checkOverflow);
    window.addEventListener('resize', checkOverflow);

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkOverflow);
      resizeObserver.disconnect();
    };
  }, [content]);

  const overflowStyles: React.CSSProperties = {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const textElement = isMobile ? (
    <span
      ref={textRef as React.RefObject<HTMLSpanElement>}
      className={className}
      style={{ ...overflowStyles, ...style }}>
      {content}
    </span>
  ) : (
    <p
      ref={textRef as React.RefObject<HTMLParagraphElement>}
      className={className}
      style={{ ...overflowStyles, ...style }}>
      {content}
    </p>
  );

  if (isOverflowing) {
    const tooltipLabel = getTooltipLabel();
    return (
      <AppTooltip
        label={tooltipLabel}
        withinPortal={false}
        openDelay={300}
        style={{
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '20px',
          color: '#000',
        }}>
        {textElement}
      </AppTooltip>
    );
  }

  return textElement;
};
